/**
 * ClosingPage — the Closing tab: picks the deal whose checklist to show and
 * renders ClosingFlow for it.
 *
 * ClosingFlow loads items for a transaction id, but the tab used to render it
 * with no id, so it always said "No closing items yet" (QA 2026-10-16). The
 * deal comes from the journey's "View Closing Checklist" button when there is
 * one, else the viewer's first deal still under way (buyer or seller side).
 *
 * @param {Object} props
 * @param {number} props.userId - Current user's person id
 * @param {string} props.role - 'buyer' | 'seller'
 * @param {number|null} [props.transactionId] - Deal chosen elsewhere (journey)
 * @returns {JSX.Element}
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useBuyerJourney, useSellerJourneys } from '../../agents/journeyAgent';
import ClosingFlow from './ClosingFlow';
import './ClosingFlow.css';

/** Deals whose checklist still matters: not recorded as ended. */
const isUnderWay = (j) => (j?.transaction?.status || '').toLowerCase() !== 'terminated';

export default function ClosingPage({ userId, role, transactionId = null }) {
  const isSeller = role === 'seller';
  const buyer = useBuyerJourney(isSeller ? null : userId);
  const seller = useSellerJourneys(isSeller ? userId : null);
  const { journeys, loading, error } = isSeller ? seller : buyer;

  const deals = useMemo(() => (journeys || []).filter(isUnderWay), [journeys]);
  const [selected, setSelected] = useState(transactionId);

  useEffect(() => {
    if (transactionId) {
      setSelected(transactionId);
    } else if (!selected && deals.length > 0) {
      setSelected(deals[0].transaction_id);
    }
  }, [transactionId, deals, selected]);

  if (!selected && loading) {
    return (
      <div className="closing-flow">
        <p className="closing-loading">Loading your deals…</p>
      </div>
    );
  }

  if (!selected && error) {
    return (
      <div className="closing-flow">
        <div className="closing-error" role="alert">{error}</div>
      </div>
    );
  }

  if (!selected) {
    return (
      <div className="closing-flow">
        <div className="closing-empty" data-testid="closing-no-deal">
          <span className="closing-empty-icon">📦</span>
          <h3>No deal under way</h3>
          <p>Your closing checklist appears here once an offer is accepted.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {deals.length > 1 && (
        <div className="closing-deal-picker">
          <label htmlFor="closing-deal">Deal</label>
          <select
            id="closing-deal"
            value={selected}
            onChange={(e) => setSelected(Number(e.target.value))}
          >
            {deals.map((j) => (
              <option key={j.transaction_id} value={j.transaction_id}>
                {j.property_address || `Transaction #${j.transaction_id}`}
              </option>
            ))}
          </select>
        </div>
      )}
      <ClosingFlow transactionId={selected} buyerId={userId} />
    </>
  );
}
